import * as THREE from 'three';
import { ProjectileManager } from './ProjectileManager';
import { ProjectileType } from './AbstractProjectile';
import { SpatialManager } from '../optimization/SpatialManager';
import { ObjectManager } from '../objects/ObjectManager';

/**
 * Integration class for the projectile system with the main game engine.
 * Provides a clean interface for the game engine to create and manage projectiles.
 */
export class ProjectileSystem {
  private projectileManager: ProjectileManager;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private spatialManager: SpatialManager | null = null;
  
  // Performance tracking
  private perfTrackingEnabled: boolean = false;
  private lastStatsUpdate: number = 0;
  private statsUpdateInterval: number = 1000; // milliseconds
  
  /**
   * Creates a new ProjectileSystem
   * @param scene The scene to render projectiles in
   * @param camera The camera for frustum culling
   * @param objectManager Optional ObjectManager to get the SpatialManager from
   */
  constructor(scene: THREE.Scene, camera: THREE.Camera, objectManager?: ObjectManager) {
    this.scene = scene;
    this.camera = camera;
    
    // Initialize the projectile manager
    this.projectileManager = ProjectileManager.getInstance(scene);
    this.projectileManager.setCamera(camera);
    
    // Set spatial manager if objectManager is provided
    if (objectManager) {
      this.setSpatialManagerFromObjectManager(objectManager);
    }
    
    console.log('ProjectileSystem initialized');
  }
  
  /**
   * Set the spatial manager from the ObjectManager
   */
  public setSpatialManagerFromObjectManager(objectManager: ObjectManager): void {
    try {
      this.spatialManager = objectManager.getSpatialManager();
      if (this.spatialManager) {
        this.projectileManager.setSpatialManager(this.spatialManager);
        console.log('ProjectileSystem: SpatialManager connected for collision detection');
      }
    } catch (error) {
      console.error('Failed to get SpatialManager from ObjectManager:', error);
    }
  }
  
  /**
   * Set the spatial manager directly
   */
  public setSpatialManager(spatialManager: SpatialManager): void {
    this.spatialManager = spatialManager;
    this.projectileManager.setSpatialManager(spatialManager);
    console.log('ProjectileSystem: SpatialManager set for collision detection');
  }
  
  /**
   * Updates the projectile system
   * @param deltaTime Time in seconds since the last update
   * @param currentTime Current time in milliseconds (for performance tracking)
   */
  public update(deltaTime: number, currentTime: number = performance.now()): void {
    // Update the projectile manager
    this.projectileManager.update(deltaTime);
    
    // Performance tracking - log stats at regular intervals
    if (this.perfTrackingEnabled && currentTime - this.lastStatsUpdate > this.statsUpdateInterval) {
      this.lastStatsUpdate = currentTime;
      this.logPerformanceStats();
    }
  }
  
  /**
   * Fires a projectile
   * @param type The type of projectile to fire
   * @param position Starting position
   * @param direction Direction vector
   * @param speed Speed in units per second
   * @param damage Amount of damage the projectile deals
   * @param target Optional target for guided projectiles
   * @returns The created projectile or null if creation failed
   */
  public fireProjectile(
    type: ProjectileType,
    position: THREE.Vector3,
    direction: THREE.Vector3,
    speed: number = 30,
    damage: number = 10,
    target?: THREE.Vector3
  ): any {
    console.log(`ProjectileSystem.fireProjectile called with type: ${type}, speed: ${speed}, damage: ${damage}`);
    
    // Normalize direction and apply speed
    const velocity = direction.clone().normalize().multiplyScalar(speed);
    console.log(`Calculated velocity: (${velocity.x.toFixed(2)}, ${velocity.y.toFixed(2)}, ${velocity.z.toFixed(2)})`);
    
    // Create the projectile
    const projectile = this.projectileManager.createProjectile(
      type,
      position,
      velocity,
      damage,
      target
    );
    
    if (projectile) {
      console.log('Projectile created successfully');
    } else {
      console.warn('Failed to create projectile');
    }
    
    return projectile;
  }
  
  /**
   * Sets the performance tracking state
   * @param enabled Whether to enable performance tracking
   * @param interval How often to log stats in milliseconds
   */
  public setPerformanceTracking(enabled: boolean, interval: number = 1000): void {
    this.perfTrackingEnabled = enabled;
    this.statsUpdateInterval = interval;
  }
  
  /**
   * Log performance statistics to the console
   */
  private logPerformanceStats(): void {
    const stats = this.projectileManager.getStats();
    console.log(`Projectile Stats - Active: ${stats.totalActive}, Visible: ${stats.visible}, Culled: ${stats.culled}`);
  }
  
  /**
   * Sets batch configuration settings
   * @param batchSize Number of projectiles to process per frame
   * @param updateInterval Process projectiles every N frames
   */
  public configureBatchProcessing(batchSize: number, updateInterval: number): void {
    this.projectileManager.setBatchConfig(batchSize, updateInterval);
  }
  
  /**
   * Sets the LOD distance thresholds
   * @param nearDistance Distance for high detail
   * @param mediumDistance Distance for medium detail
   */
  public configureLODDistances(nearDistance: number, mediumDistance: number): void {
    this.projectileManager.setLODDistances(nearDistance, mediumDistance);
  }
  
  /**
   * Enable or disable frustum culling
   */
  public setCullingEnabled(enabled: boolean): void {
    this.projectileManager.setCullingEnabled(enabled);
  }
  
  /**
   * Cleanup and release all projectiles
   */
  public cleanup(): void {
    this.projectileManager.releaseAllProjectiles();
  }
} 